import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { FmnWatermark } from "../FmnWatermark";

const FADE = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-100px" },
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] },
};

const EDITIONS = [
    {
        key: "elders",
        src: "/brand/packaging/inbox/adult-box-open-inbox.png",
        accent: "#B8B4D9",
        testid: "packaging-elders",
    },
    {
        key: "kids_pink",
        src: "/brand/packaging/inbox/kids-box-open-inbox.png",
        accent: "#F4D4D6",
        testid: "packaging-kids-pink",
    },
    {
        key: "kids_blue",
        src: "/brand/packaging/inbox/blue-box-open-inbox.png",
        accent: "#C9DCEB",
        testid: "packaging-kids-blue",
    },
];

export function PackagingShowcase() {
    const { t } = useTranslation();
    return (
        <section
            className="relative py-28 px-6 bg-[#FDFBF7] overflow-hidden"
            data-testid="packaging-showcase"
        >
            <FmnWatermark position="right" size={720} opacity={0.05} rotate={-14} />
            <div className="max-w-7xl mx-auto">
                <motion.div {...FADE} className="text-center max-w-3xl mx-auto mb-16">
                    <p className="font-sans text-xs uppercase tracking-[0.3em] text-[#6B7DB8] font-semibold mb-5">
                        {t("packaging.eyebrow")}
                    </p>
                    <h2 className="font-serif text-4xl sm:text-5xl text-[#2D2B2A] leading-tight">
                        {t("packaging.headline_part1")}{" "}
                        <em className="italic text-[#4A5A95]">
                            {t("packaging.headline_emphasis")}
                        </em>
                    </h2>
                    <p className="mt-8 text-lg text-[#5C5A59] leading-relaxed font-light">
                        {t("packaging.subhead")}
                    </p>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10">
                    {EDITIONS.map((ed, i) => (
                        <motion.figure
                            key={ed.key}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{
                                duration: 0.9,
                                ease: [0.16, 1, 0.3, 1],
                                delay: i * 0.12,
                            }}
                            className="group"
                            data-testid={ed.testid}
                        >
                            <div
                                className="aspect-square rounded-3xl overflow-hidden bg-white border border-[#E5E1D8] shadow-rem-card transition-all duration-700 group-hover:-translate-y-1 group-hover:shadow-2xl"
                                style={{ boxShadow: `0 1px 0 ${ed.accent}40 inset` }}
                            >
                                <img
                                    src={ed.src}
                                    alt={t(`packaging.${ed.key}.alt`)}
                                    className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.03]"
                                    loading="lazy"
                                />
                            </div>
                            <figcaption className="mt-6 text-center">
                                <p
                                    className="font-sans text-xs uppercase tracking-[0.3em] font-semibold mb-2"
                                    style={{ color: ed.accent }}
                                >
                                    {t(`packaging.${ed.key}.label`)}
                                </p>
                                <p className="font-serif italic text-lg text-[#2D2B2A] leading-snug">
                                    {t(`packaging.${ed.key}.tagline`)}
                                </p>
                            </figcaption>
                        </motion.figure>
                    ))}
                </div>

                {/* Emotional payoff — dad + toddler on the couch */}
                <motion.div
                    {...FADE}
                    transition={{ ...FADE.transition, delay: 0.2 }}
                    className="mt-24 lg:mt-32 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center"
                    data-testid="packaging-payoff"
                >
                    <div className="lg:col-span-7 order-2 lg:order-1">
                        <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-rem-card">
                            <img
                                src="/brand/product/kids-blue-inhand.png"
                                alt={t("packaging.payoff.alt")}
                                className="w-full h-full object-cover"
                                loading="lazy"
                            />
                        </div>
                    </div>
                    <div className="lg:col-span-5 order-1 lg:order-2">
                        <p className="font-sans text-xs uppercase tracking-[0.3em] text-[#6B7DB8] font-semibold mb-5">
                            {t("packaging.payoff.eyebrow")}
                        </p>
                        <h3 className="font-serif text-3xl sm:text-4xl text-[#2D2B2A] leading-tight">
                            {t("packaging.payoff.headline_part1")}{" "}
                            <em className="italic text-[#4A5A95]">
                                {t("packaging.payoff.headline_emphasis")}
                            </em>
                        </h3>
                        <p className="mt-6 text-[#5C5A59] text-lg leading-relaxed font-light">
                            {t("packaging.payoff.body")}
                        </p>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
